// Client de POST /api/analyze : envoie un `blobId`, reçoit des aliments AU FIL DE L'EAU.
//
// L'analyse dure 3 à 8 s. Le seul contrat qui compte ici : `surAliment` est appelé pendant
// l'analyse, pas après — un écran qui ne bouge pas pendant huit secondes est un écran qu'on croit
// planté (README « Partis pris »). `surReprise` est l'autre moitié du contrat : quand le serveur a
// dû rejouer une sortie hors schéma, tout ce qui a déjà été affiché est caduc et la liste doit être
// vidée.
import { lireFluxSse } from './sseStream.js';
import type { AlimentDetecte, AnalyzeDone } from './types.js';

export interface EcouteursAnalyse {
  surAliment(aliment: AlimentDetecte, index: number): void;
  /** La sortie du modèle était hors schéma : vider la liste, le serveur rejoue. */
  surReprise?(info: { tentative: number; raison: string }): void;
  surFin?(done: AnalyzeDone): void;
}

/**
 * `photo_absente` : le blob n'est plus lisible côté serveur (rétention, SAS expirée avant le PUT) —
 *   reprendre la photo, pas se contenter de rejouer l'analyse.
 * `rate_limited` : plafond horaire/journalier de l'utilisateur atteint (429, api/src/rateLimit.ts) —
 *   refus temporaire, pas la peine de rejouer avant `retryAfterSeconds`.
 * `budget_exceeded` : plafond de dépense quotidien de NutriScan (ou de cette IP en mode public)
 *   atteint (402, api/src/budgetGuard.ts) — refus jusqu'au lendemain, rejouer ne changera rien avant.
 * `turnstile` : jeton Turnstile absent/invalide/expiré (403, api/src/turnstile.ts, mode public
 *   seulement) — rejouer avec un jeton frais peut réussir, contrairement aux deux précédents.
 * Aucun des trois ne doit se voir proposer une reprise IMMÉDIATE (web/src/main.ts).
 */
export type AnalyseErrorCode = 'photo_absente' | 'rate_limited' | 'budget_exceeded' | 'turnstile';

/** L'analyse n'a pas abouti : message déjà destiné à l'utilisateur (jamais une trace technique). */
export class AnalyseError extends Error {
  readonly code?: AnalyseErrorCode;
  /** Renseigné seulement pour `rate_limited` (api/src/rateLimit.ts, en-tête Retry-After). */
  readonly retryAfterSeconds?: number;

  constructor(message: string, code?: AnalyseErrorCode, retryAfterSeconds?: number) {
    super(message);
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** `90` -> « moins d'une minute », `900` -> « 15 min ». Toujours arrondi au-dessus : jamais dire
 * « réessaie dans 4 min » et refuser encore l'appel fait pile à la 4e minute. */
function formaterDelaiAttente(retryAfterSeconds: number): string {
  const minutes = Math.ceil(retryAfterSeconds / 60);
  return minutes <= 1 ? "moins d'une minute" : `${minutes} min`;
}

export async function analyserPhoto(
  blobId: string,
  ecouteurs: EcouteursAnalyse,
  options: {
    baseUrl?: string;
    signal?: AbortSignal;
    fetchImpl?: typeof fetch;
    /** Jeton Turnstile (CLAUDE.md § Auth mode public) — absent en mode access, ignoré côté API. */
    turnstileToken?: string;
  } = {},
): Promise<AnalyzeDone> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const reponse = await fetchImpl(`${options.baseUrl ?? ''}/api/analyze`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ blobId, ...(options.turnstileToken ? { turnstileToken: options.turnstileToken } : {}) }),
    signal: options.signal,
  });

  if (!reponse.ok) {
    // Une erreur AVANT le flux (photo absente, plafond de débit ou de budget, Turnstile, config
    // manquante) est un statut HTTP ordinaire : les évènements `error` ci-dessous ne concernent que
    // ce qui échoue une fois le flux ouvert.
    const detail = (await reponse.json().catch(() => null)) as {
      error?: string;
      retryAfterSeconds?: number;
    } | null;
    if (detail?.error === 'not_found') {
      throw new AnalyseError("La photo n'est plus disponible : reprends-la.", 'photo_absente');
    }
    if (detail?.error === 'rate_limited') {
      const retryAfterSeconds = detail.retryAfterSeconds ?? 3600;
      throw new AnalyseError(
        `Trop d'analyses pour le moment : réessaie dans ${formaterDelaiAttente(retryAfterSeconds)}.`,
        'rate_limited',
        retryAfterSeconds,
      );
    }
    if (detail?.error === 'budget_exceeded') {
      throw new AnalyseError(
        'Le plafond de dépense quotidien de NutriScan est atteint : réessaie demain.',
        'budget_exceeded',
      );
    }
    if (detail?.error === 'turnstile_required' || detail?.error === 'turnstile_invalid') {
      throw new AnalyseError('Vérification anti-robot invalide ou expirée : réessaie.', 'turnstile');
    }
    throw new AnalyseError("L'analyse est indisponible pour le moment.");
  }
  if (!reponse.body) throw new AnalyseError("L'analyse est indisponible pour le moment.");

  let index = 0;
  let fin: AnalyzeDone | null = null;
  for await (const evenement of lireFluxSse(reponse.body)) {
    const donnees: unknown = JSON.parse(evenement.donnees);
    if (evenement.nom === 'aliment') {
      ecouteurs.surAliment(donnees as AlimentDetecte, index);
      index++;
    } else if (evenement.nom === 'reprise') {
      index = 0;
      ecouteurs.surReprise?.(donnees as { tentative: number; raison: string });
    } else if (evenement.nom === 'done') {
      fin = donnees as AnalyzeDone;
      ecouteurs.surFin?.(fin);
    } else if (evenement.nom === 'error') {
      throw new AnalyseError((donnees as { message: string }).message);
    }
  }

  if (!fin) throw new AnalyseError("L'analyse s'est interrompue avant la fin.");
  return fin;
}

/** Remonte une portion corrigée par l'utilisateur (curseur ou saisie) — POST /api/correction. */
export async function envoyerCorrection(
  correction: {
    analyseId: string;
    aliment: string;
    estimationModele: { poids_g_min: number; poids_g_max: number };
    valeurCorrigeeG: number;
    origine: 'curseur' | 'saisie';
    blobId?: string;
  },
  options: { baseUrl?: string } = {},
): Promise<boolean> {
  const reponse = await fetch(`${options.baseUrl ?? ''}/api/correction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(correction),
  });
  return reponse.ok;
}
